/**
 * KingMessageModal - shown to the current top-1 player so they can submit a
 * short message that will be broadcast on the main-menu marquee.
 *
 * Opens with { claimId, previousMessage? }. The user types up to 160 graphemes
 * (counted via Intl.Segmenter, so emojis and combining marks count as 1) and
 * either publishes or skips. Errors from the server are surfaced via toasts.
 */

import { mountToAppShell } from '../appShell'
import { NetworkManager } from '../../network/NetworkManager'
import { FONTS, NeonColors, neonPanel } from '../neon-theme'

export interface KingMessageModalOptions {
  claimId: string
  previousMessage?: string
  /** Fired after a successful submit (parent should refetch /current). */
  onSubmitted?: () => void
  /** Fired whenever the modal closes for any reason (X, submit, skip, error). */
  onClose?: () => void
}

const MAX_GRAPHEMES = 160

type SubmitErrorCode =
  | 'COOLDOWN'
  | 'URL_FORBIDDEN'
  | 'STALE_CLAIM'
  | 'NOT_CURRENT_KING'
  | 'NO_PENDING'
  | 'NO_CURRENT_KING'
  | 'ALREADY_SUBMITTED'

interface ApiResponse {
  success: boolean
  error?: string
}

function countGraphemes(text: string): number {
  if (!text) return 0
  // Intl.Segmenter is supported in all modern browsers + Telegram WebView.
  // Fall back to Array.from (code-point count) if absent.
  const SegmenterCtor = (Intl as unknown as { Segmenter?: typeof Intl.Segmenter }).Segmenter
  if (SegmenterCtor) {
    const segmenter = new SegmenterCtor('und', { granularity: 'grapheme' })
    let n = 0
    for (const _ of segmenter.segment(text)) n++
    return n
  }
  return Array.from(text).length
}

export class KingMessageModal {
  private container: HTMLDivElement | null = null
  private textarea: HTMLTextAreaElement | null = null
  private submitBtn: HTMLButtonElement | null = null
  private skipBtn: HTMLButtonElement | null = null
  private counterEl: HTMLSpanElement | null = null
  private busy = false
  private closed = false

  constructor(private readonly opts: KingMessageModalOptions) {}

  show(): void {
    if (this.container) return

    const overlay = document.createElement('div')
    overlay.style.cssText = `
      position: absolute; inset: 0; z-index: 9990;
      display: flex; align-items: center; justify-content: center;
      padding: 24px;
      background: rgba(0,2,10,0.82);
      backdrop-filter: blur(8px);
      font-family: ${FONTS.body};
    `
    this.container = overlay

    const card = document.createElement('div')
    card.style.cssText = `
      ${neonPanel({ blur: true })}
      position: relative;
      width: min(420px, 100%);
      padding: 24px 22px 20px;
      border-radius: 20px;
      border: 1px solid rgba(251,191,36,0.35);
      box-shadow: 0 24px 80px rgba(0,0,0,0.55), 0 0 32px rgba(251,191,36,0.18);
    `
    overlay.appendChild(card)

    // Close (X) button
    const closeBtn = document.createElement('button')
    closeBtn.textContent = '×'
    closeBtn.style.cssText = `
      position: absolute; top: 8px; right: 12px;
      background: transparent; border: none;
      color: ${NeonColors.textMuted}; font-size: 1.4rem;
      cursor: pointer; padding: 4px 8px;
    `
    closeBtn.addEventListener('click', () => this.close())
    card.appendChild(closeBtn)

    // Title
    const title = document.createElement('div')
    title.textContent = '\u{1F3C6} Ты #1!'
    title.style.cssText = `
      color: ${NeonColors.primary};
      font-weight: 900;
      font-size: 1.3rem;
      text-align: center;
      letter-spacing: 0.05em;
      margin-bottom: 6px;
      text-shadow: 0 0 12px ${NeonColors.primaryGlow};
    `
    card.appendChild(title)

    const subtitle = document.createElement('div')
    subtitle.textContent = 'Напиши сообщение для всех игроков'
    subtitle.style.cssText = `
      text-align: center;
      color: ${NeonColors.textMuted};
      font-size: 0.85rem;
      margin-bottom: 14px;
    `
    card.appendChild(subtitle)

    // Textarea
    const textarea = document.createElement('textarea')
    textarea.rows = 3
    // textarea.placeholder uses textContent-style escaping; safe for user input.
    textarea.placeholder = this.opts.previousMessage || 'Привет мир!'
    textarea.style.cssText = `
      width: 100%; box-sizing: border-box;
      padding: 12px 14px;
      background: rgba(15,23,42,0.6);
      border: 2px solid rgba(71,85,105,0.5);
      border-radius: 12px;
      color: ${NeonColors.textBright};
      font-size: 1rem;
      font-family: ${FONTS.body};
      resize: vertical;
      outline: none;
      transition: border-color 0.2s;
    `
    textarea.addEventListener('focus', () => {
      textarea.style.borderColor = 'rgba(251,191,36,0.5)'
    })
    textarea.addEventListener('blur', () => {
      textarea.style.borderColor = 'rgba(71,85,105,0.5)'
    })
    textarea.addEventListener('input', () => this.updateCounter())
    card.appendChild(textarea)
    this.textarea = textarea

    // Counter
    const counterWrap = document.createElement('div')
    counterWrap.style.cssText = `
      display: flex; justify-content: flex-end;
      margin-top: 6px; margin-bottom: 14px;
      font-size: 0.78rem;
      color: ${NeonColors.textMuted};
    `
    const counter = document.createElement('span')
    counter.textContent = `0 / ${MAX_GRAPHEMES}`
    counterWrap.appendChild(counter)
    card.appendChild(counterWrap)
    this.counterEl = counter

    // Buttons row
    const row = document.createElement('div')
    row.style.cssText = 'display: flex; gap: 10px;'

    const submitBtn = document.createElement('button')
    submitBtn.textContent = 'Опубликовать'
    submitBtn.style.cssText = `
      flex: 1; padding: 12px 0;
      background: ${NeonColors.primary}; color: #0f172a;
      border: none; border-radius: 12px;
      font-weight: 900; font-size: 0.95rem; letter-spacing: 0.08em;
      text-transform: uppercase;
      cursor: pointer;
      box-shadow: 0 0 24px rgba(251,191,36,0.35);
      font-family: ${FONTS.body};
      transition: opacity 0.2s, transform 0.1s;
    `
    submitBtn.addEventListener('click', () => { void this.handleSubmit() })
    row.appendChild(submitBtn)
    this.submitBtn = submitBtn

    const skipBtn = document.createElement('button')
    skipBtn.textContent = 'Пропустить'
    skipBtn.style.cssText = `
      flex: 1; padding: 12px 0;
      background: transparent; color: ${NeonColors.textMuted};
      border: 1px solid ${NeonColors.textDim}; border-radius: 12px;
      font-weight: 700; font-size: 0.9rem; letter-spacing: 0.05em;
      cursor: pointer;
      font-family: ${FONTS.body};
      transition: color 0.2s, border-color 0.2s;
    `
    skipBtn.addEventListener('click', () => { void this.handleSkip() })
    row.appendChild(skipBtn)
    this.skipBtn = skipBtn

    card.appendChild(row)

    mountToAppShell(overlay)
    this.updateCounter()
    // Auto-focus the textarea for instant typing.
    setTimeout(() => textarea.focus(), 50)
  }

  /** Public alias for close — symmetric API with other dialogs. */
  destroy(): void {
    this.close()
  }

  private close(): void {
    if (this.closed) return
    this.closed = true
    if (this.container) {
      this.container.remove()
      this.container = null
    }
    this.textarea = null
    this.submitBtn = null
    this.skipBtn = null
    this.counterEl = null
    this.opts.onClose?.()
  }

  private updateCounter(): void {
    if (!this.textarea || !this.counterEl || !this.submitBtn) return
    const text = this.textarea.value
    const count = countGraphemes(text)
    this.counterEl.textContent = `${count} / ${MAX_GRAPHEMES}`
    const over = count > MAX_GRAPHEMES
    this.counterEl.style.color = over ? NeonColors.danger : NeonColors.textMuted
    const disable = over || this.busy
    this.submitBtn.disabled = disable
    this.submitBtn.style.opacity = disable ? '0.5' : '1'
    this.submitBtn.style.cursor = disable ? 'not-allowed' : 'pointer'
  }

  private setBusy(busy: boolean): void {
    this.busy = busy
    if (this.submitBtn) {
      this.submitBtn.disabled = busy || (countGraphemes(this.textarea?.value ?? '') > MAX_GRAPHEMES)
      this.submitBtn.style.opacity = this.submitBtn.disabled ? '0.5' : '1'
    }
    if (this.skipBtn) {
      this.skipBtn.disabled = busy
      this.skipBtn.style.opacity = busy ? '0.5' : '1'
    }
    if (this.textarea) {
      this.textarea.disabled = busy
    }
  }

  private async handleSubmit(): Promise<void> {
    if (this.busy || !this.textarea) return
    const message = this.textarea.value.trim()
    if (!message) return
    if (countGraphemes(message) > MAX_GRAPHEMES) return

    const token = NetworkManager.getInstance().getToken()
    if (!token) {
      this.toast('Нужна авторизация')
      return
    }

    this.setBusy(true)
    try {
      const res = await fetch('/api/king-message/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message, claimId: this.opts.claimId }),
      })
      let data: ApiResponse | null = null
      try { data = await res.json() as ApiResponse } catch { /* ignore */ }

      if (data?.success) {
        this.toast('Отправлено!', 'success')
        this.opts.onSubmitted?.()
        this.close()
        return
      }

      this.handleErrorCode(data?.error as SubmitErrorCode | undefined)
    } catch {
      this.toast('Ошибка сети')
    } finally {
      this.setBusy(false)
    }
  }

  private async handleSkip(): Promise<void> {
    if (this.busy) return
    const token = NetworkManager.getInstance().getToken()
    if (!token) {
      this.close()
      return
    }
    this.setBusy(true)
    try {
      await fetch('/api/king-message/skip', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ claimId: this.opts.claimId }),
      })
    } catch {
      // Skip is fire-and-forget from the user's perspective.
    } finally {
      this.setBusy(false)
      this.close()
    }
  }

  private handleErrorCode(code: SubmitErrorCode | undefined): void {
    switch (code) {
      case 'URL_FORBIDDEN':
        this.toast('Ссылки запрещены')
        return
      case 'COOLDOWN':
        this.toast('Можно через 5 минут')
        return
      case 'STALE_CLAIM':
      case 'NOT_CURRENT_KING':
      case 'NO_CURRENT_KING':
      case 'NO_PENDING':
        this.toast('Ты больше не #1')
        this.close()
        return
      case 'ALREADY_SUBMITTED':
        this.toast('Уже отправили')
        this.close()
        return
      default:
        this.toast('Не удалось отправить')
    }
  }

  private toast(message: string, type: 'success' | 'error' = 'error'): void {
    const color = type === 'success' ? NeonColors.success : NeonColors.danger
    const toast = document.createElement('div')
    toast.style.cssText = `
      position: absolute; top: 16px; left: 50%;
      transform: translateX(-50%);
      background: ${color}; color: #fff;
      padding: 10px 20px; border-radius: 12px;
      font-size: 0.9rem; z-index: 10000;
      box-shadow: 0 0 20px ${color}40;
      transition: opacity 0.3s;
      font-family: ${FONTS.body};
    `
    toast.textContent = message
    mountToAppShell(toast)
    setTimeout(() => {
      toast.style.opacity = '0'
      setTimeout(() => toast.remove(), 300)
    }, 2800)
  }
}
